import {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import _ from 'lodash';
import {updateSiteRealtime} from "../store/features/sites/sitesSlice.js";
import {useSocket} from "../dashboard/SocketContext.jsx";

export const useSiteMonitoring = () => {
    const dispatch = useDispatch();
    const socketContext = useSocket();

    // Utiliser useRef pour éviter les dépendances circulaires
    const monitoringSitesRef = useRef(new Set());
    const [monitoringSites, setMonitoringSites] = useState(new Set());

    // Sécuriser les fonctions du socket
    const joinSite = socketContext?.joinSite || _.noop;
    const leaveSite = socketContext?.leaveSite || _.noop;
    const realtimeStats = socketContext?.realtimeStats || {};
    const on = socketContext?.on || _.noop;
    const off = socketContext?.off || _.noop;

    // Mettre à jour la ref quand monitoringSites change
    useEffect(() => {
        monitoringSitesRef.current = monitoringSites;
    }, [monitoringSites]);

    // Surveiller un site - utilisation de useCallback SANS monitoringSites dans les dépendances
    const monitorSite = useCallback((siteKey) => {
        if (!siteKey) return;

        const safeSiteKey = _.toString(siteKey);

        // Utiliser la ref au lieu de la state
        if (!monitoringSitesRef.current.has(safeSiteKey)) {
            console.log(`👁️  Début surveillance site: ${safeSiteKey}`);

            if (_.isFunction(joinSite)) {
                joinSite(safeSiteKey);
            }

            // Mettre à jour l'état
            setMonitoringSites(prev => {
                const newSet = new Set(prev);
                newSet.add(safeSiteKey);
                return newSet;
            });
        }
    }, [joinSite]); // NE PAS inclure monitoringSites ici

    // Surveiller plusieurs sites
    const monitorSites = useCallback((siteKeys) => {
        if (!_.isArray(siteKeys)) return;

        _.forEach(siteKeys, siteKey => {
            if (siteKey) {
                monitorSite(siteKey);
            }
        });
    }, [monitorSite]); // Dépend uniquement de monitorSite

    // Arrêter la surveillance d'un site
    const stopMonitoringSite = useCallback((siteKey) => {
        if (!siteKey) return;

        const safeSiteKey = _.toString(siteKey);

        if (monitoringSitesRef.current.has(safeSiteKey)) {
            console.log(`👋 Arrêt surveillance site: ${safeSiteKey}`);

            if (_.isFunction(leaveSite)) {
                leaveSite(safeSiteKey);
            }

            setMonitoringSites(prev => {
                const newSet = new Set(prev);
                newSet.delete(safeSiteKey);
                return newSet;
            });
        }
    }, [leaveSite]); // NE PAS inclure monitoringSites ici

    // Arrêter tous les sites
    const stopAllMonitoring = useCallback(() => {
        monitoringSitesRef.current.forEach(siteKey => {
            if (siteKey && _.isFunction(leaveSite)) {
                leaveSite(siteKey);
            }
        });

        setMonitoringSites(new Set());
    }, [leaveSite]);

    // Écouter les mises à jour en temps réel
    useEffect(() => {
        if (!_.isFunction(on) || !_.isFunction(off)) {
            console.warn('❌ Les fonctions on/off ne sont pas disponibles');
            return;
        }

        const handleVisitUpdate = (data) => {
            if (!_.isObject(data)) return;

            const siteId = _.get(data, 'siteId');
            if (siteId) {
                const updateData = {
                    siteKey: siteId,
                    todayVisites: _.toNumber(_.get(data, 'today', 0)),
                    totalVisites: _.toNumber(_.get(data, 'total', 0)),
                    onlineVisitors: _.toNumber(_.get(data, 'online', 0)),
                    peakVisitors: _.toNumber(_.get(data, 'peak', 0)),
                    lastUpdated: _.get(data, 'lastUpdated', new Date().toISOString()),
                    status: _.toNumber(_.get(data, 'online', 0)) > 0 ? 'active' : 'inactive'
                };

                dispatch(updateSiteRealtime(updateData));
            }
        };

        on('visit:update', handleVisitUpdate);

        return () => {
            off('visit:update', handleVisitUpdate);
        };
    }, [dispatch, on, off]);

    // Mettre à jour les stats depuis realtimeStats - CORRIGÉ
    useEffect(() => {
        // Vérifier si realtimeStats a changé
        const statsEntries = Object.entries(realtimeStats);

        if (statsEntries.length === 0) return;

        // Filtrer uniquement les sites qu'on surveille
        const sitesToUpdate = statsEntries.filter(([siteKey]) =>
            monitoringSitesRef.current.has(siteKey)
        );

        sitesToUpdate.forEach(([siteKey, stats]) => {
            if (_.isObject(stats)) {
                const updateData = {
                    siteKey,
                    todayVisites: _.toNumber(_.get(stats, 'today', 0)),
                    totalVisites: _.toNumber(_.get(stats, 'total', 0)),
                    onlineVisitors: _.toNumber(_.get(stats, 'online', 0)),
                    peakVisitors: _.toNumber(_.get(stats, 'peak', 0)),
                    lastUpdated: _.get(stats, 'lastUpdated', new Date().toISOString()),
                    status: _.toNumber(_.get(stats, 'online', 0)) > 0 ? 'active' : 'inactive'
                };

                dispatch(updateSiteRealtime(updateData));
            }
        });
    }, [realtimeStats, dispatch]); // Ne pas inclure monitoringSites ici

    return {
        monitoringSites: Array.from(monitoringSites),
        monitorSite,
        stopMonitoringSite,
        monitorSites,
        stopAllMonitoring,
        realtimeStats
    };
};
